import { graphql, useStaticQuery } from 'gatsby';
import BazaarvoiceWidget from 'gatsby-plugin-bazaarvoice/BazaarvoiceWidget';
import { IBV } from 'gatsby-plugin-bazaarvoice/types';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StorageFeedingGuide from 'src/cms-components/StorageFeedingGuide/StorageFeedingGuide';
import { Link } from 'src/components/Link';
import {
  PurinaAccordion,
  PurinaAccordionContent,
  PurinaAccordionItem,
  PurinaAccordionTrigger,
} from 'src/components/accordion/Accordion';
import { useAutoScrollMultiple } from 'src/components/accordion/hooks';
import IconList from 'src/components/common/IconList';
import ProductIngredients from 'src/components/product/ProductIngredients';
import RichText from 'src/components/rich_text/RichText';
import { NodeProductProps } from '../types';

const accordionKeys = ['overview', 'ingredients', 'feeding-and-nutrition', 'reviews'];

interface Props {
  location: any;
  node: NodeProductProps;
  availableSizes: {
    text: string;
    icon: {
      url: string;
      alt: string;
    } | null;
  }[];
  labelDeck?: string | undefined;
  showFeedingTab?: boolean | undefined;
  bvID: string;
}

const useGetFeedingGuideQuery = (langcode: string) =>
  useStaticQuery<{ allStorageFeedingGuide: { edges: { node: IStorageFeedingGuide }[] } }>(graphql`
    query {
      allStorageFeedingGuide {
        edges {
          node {
            ...StorageFeedingGuide
          }
        }
      }
    }
  `).allStorageFeedingGuide.edges.filter(({ node }) => node.langcode === langcode);

const OverView: React.FC<Pick<Props, 'node' | 'availableSizes'>> = ({ node, availableSizes }) => {
  const { t } = useTranslation();
  return (
    <div className="pds-flex pds-flex-col pds-gap-4 md:pds-flex-row md:pds-gap-5">
      <div className="pds-flex pds-w-full pds-flex-col">
        {node?.highlights.length > 0 && (
          <>
            <h3 className="pds-text-title-sm">{t('Highlights')}</h3>
            <ul className="pds-list-disc">
              {node.highlights.map((highlight, index) => {
                return <li key={index}>{highlight}</li>;
              })}
            </ul>
          </>
        )}
        {node.body?.processed && (
          <>
            <h3 className="pds-text-title-sm">{t('Product Description')}</h3>
            <RichText body={node.body.processed} />
          </>
        )}
      </div>
      {availableSizes.length >= 1 && (
        <div className="pds-w-full">
          <h3 className="pds-text-body-lg pds-font-semibold">{t('Available Sizes')}</h3>
          <IconList items={availableSizes} size="small" />
        </div>
      )}
    </div>
  );
};

const Ingredients: React.FC<Pick<Props, 'node' | 'labelDeck'>> = ({ labelDeck, node }) => {
  const { t } = useTranslation();
  return (
    <>
      <ProductIngredients headingLevel="h3" ingredients={node.relationships.ingredients!} />
      {labelDeck && (
        <div className="mt-4">
          <Link to={labelDeck} target="_blank" rel="noreferrer">
            {t('Download the full ingredient list (PDF)')}
          </Link>
        </div>
      )}
    </>
  );
};

const Feeding: React.FC<Pick<Props, 'node' | 'labelDeck'>> = ({ node, labelDeck }) => {
  const [feedingGuide = null] = useGetFeedingGuideQuery(node.langcode);
  const { t } = useTranslation();
  return (
    <>
      <h3 className="pds-text-title-sm">{t('Feeding Guide')}</h3>
      {feedingGuide?.node && !node.feeding_display_opt_out && (
        <StorageFeedingGuide productTitle={node.title} node={feedingGuide.node} />
      )}
      {node.feeding_instructions && <RichText body={node.feeding_instructions.processed} />}
      {labelDeck && (
        <p>
          {t('For a list of all feeding recommendations')},{' '}
          <Link
            analytics={false}
            to={labelDeck}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              sendIt({
                event: 'module_interaction',
                eventParams: {
                  component_id: node.title,
                  component_type: 'feeding_guide_pdf_link',
                  content_name: 'Download the full recommended feeding table (PDF)',
                },
              });
            }}
          >
            {t('Download the full recommended feeding table')} (PDF)
          </Link>
          .
        </p>
      )}
    </>
  );
};

export const ProductAccordions: React.FC<Props> = ({
  location,
  node,
  availableSizes,
  labelDeck,
  showFeedingTab,
  bvID,
}) => {
  const [open, setOpen] = useState<string[]>([]);
  const { setIsRemoving } = useAutoScrollMultiple({ open, setOpen, accordionKeys });

  useEffect(() => {
    window.bvCallback = (bv: IBV) => {
      bv.swat_reviews.on('show', () => {
        setOpen(pre => [...pre, 'reviews']);
        window.location.href = '#reviews';
      });
    };
  }, [location.hash]);

  return (
    <PurinaAccordion
      options={{ headingLevel: 'h2' }}
      className="pds-border-t-solid pds-border-t-line pds-mt-5 pds-border-t-medium md:pds-mt-6 [&_h2]:pds-mb-0"
      type="multiple"
      value={open}
      onValueChange={v => {
        if (v.length < open.length) {
          setIsRemoving(true);
        } else {
          const newActiveKey = v[v.length - 1];
          sendIt({
            event: 'module_interaction',
            eventParams: {
              cta_location: 'product_detail_page',
              product_name: node.title,
              content_name: newActiveKey,
              component_type: 'accordion',
              component_id: newActiveKey,
            },
          });
        }
        setOpen(v);
      }}
    >
      <PurinaAccordionItem value="overview">
        <PurinaAccordionTrigger id="overview">Overview</PurinaAccordionTrigger>
        <PurinaAccordionContent forceMount>
          <OverView node={node} availableSizes={availableSizes} />
        </PurinaAccordionContent>
      </PurinaAccordionItem>

      {node?.relationships?.ingredients && node.relationships.ingredients.length > 1 && (
        <PurinaAccordionItem value="ingredients">
          <PurinaAccordionTrigger id="ingredients">Ingredients</PurinaAccordionTrigger>
          <PurinaAccordionContent forceMount>
            <Ingredients labelDeck={labelDeck} node={node} />
          </PurinaAccordionContent>
        </PurinaAccordionItem>
      )}

      {showFeedingTab && (
        <PurinaAccordionItem value="feeding-and-nutrition">
          <PurinaAccordionTrigger id="feeding-and-nutrition">
            Feeding and Nutrition
          </PurinaAccordionTrigger>
          <PurinaAccordionContent forceMount>
            <Feeding labelDeck={labelDeck} node={node} />
          </PurinaAccordionContent>
        </PurinaAccordionItem>
      )}

      <PurinaAccordionItem value="reviews">
        <PurinaAccordionTrigger id="reviews">Reviews</PurinaAccordionTrigger>
        <PurinaAccordionContent>
          <BazaarvoiceWidget
            attributes={{
              productID: bvID,
              widgetID: 'reviews',
            }}
          />
        </PurinaAccordionContent>
      </PurinaAccordionItem>
    </PurinaAccordion>
  );
};
